import { useEffect, useMemo, useState } from "react";
import { Card } from "../components/Card";
import { Eyebrow } from "../components/Eyebrow";
import { HowTo, HowToStep } from "../components/HowTo";
import { MarketPriceLinks } from "../components/MarketPriceLinks";
import {
  getItemsForShelf,
  getShelfForItem,
  industryLabel,
  industryTone,
  ITEM_SHELVES,
} from "../lib/catalog";
import { formatYen } from "../lib/format";
import type {
  GeneratedImage,
  Industry,
  Item,
  PriceQuote,
  PriceRange,
  ProposalItem,
  ReferenceImageAsset,
} from "../lib/types";

// ロット選択の候補（相場のジャンプポイント）
const LOT_PRESETS = [100, 300, 500, 1000, 3000];
// 生成枚数の選択肢。上限は functions/api/generate-image.ts 側でも同じ値で弾く。
const GEN_COUNTS = [2, 3, 4, 5, 6];

type Props = {
  industries: Industry[];
  items: Item[];
  hideCosts?: boolean; // 商談モード: 原価・粗利・値引き下限・競合レンジを隠す
};

export function ProposalBuilder({ industries, items, hideCosts = false }: Props) {
  const [industryId, setIndustryId] = useState("");
  const [itemId, setItemId] = useState("");
  const [shelfId, setShelfId] = useState("all");
  const [qty, setQty] = useState<number>(100);

  const [loadingQuote, setLoadingQuote] = useState(false);
  const [ranges, setRanges] = useState<PriceRange[]>([]);
  const [quote, setQuote] = useState<PriceQuote | null>(null);
  const [quoteNote, setQuoteNote] = useState<string | null>(null);
  const [quoteErr, setQuoteErr] = useState<string | null>(null);
  const [quoteFallback, setQuoteFallback] = useState(false); // 他市場平均で算出した参考値か
  const [fallbackNote, setFallbackNote] = useState<string | null>(null);

  // 画像生成
  const [refImage, setRefImage] = useState<string | null>(null); // dataUrl
  const [refDragging, setRefDragging] = useState(false); // 参考画像のドラッグ中ハイライト
  const [refNote, setRefNote] = useState("");
  const [genStatus, setGenStatus] = useState<"idle" | "loading" | "done" | "error">("idle");
  const [genImages, setGenImages] = useState<GeneratedImage[]>([]);
  const [genNote, setGenNote] = useState<string | null>(null);
  const [genError, setGenError] = useState<string | null>(null);
  const [provider, setProvider] = useState<"openai" | "gemini">("openai"); // 生成AI選択（②）
  // 生成枚数。1枚あたり課金されるので、営業が案件の当たり具合に応じて絞れるようにする。
  const [genCount, setGenCount] = useState<number>(4);
  const [selectedImages, setSelectedImages] = useState<string[]>([]); // 採用画像（複数・選択順=表示順）
  const [libraryImages, setLibraryImages] = useState<ReferenceImageAsset[]>([]);
  const [libraryLoading, setLibraryLoading] = useState(false);
  const [selectedLibraryImage, setSelectedLibraryImage] = useState<ReferenceImageAsset | null>(null);

  // 確定＠単価の手入力（キー=ロット行index、値=確定単価。空=推奨を採用）
  const [priceOverrides, setPriceOverrides] = useState<Record<number, number | "">>({});

  // 提案カゴ（商品行）
  const [basket, setBasket] = useState<ProposalItem[]>([]);
  const [saveState, setSaveState] = useState<string | null>(null);

  // 提案書ヘッダ（PDF・保存の両方で使用）
  const [clientName, setClientName] = useState("");
  const [salesRep, setSalesRep] = useState("");
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfMenu, setPdfMenu] = useState<"proposal" | "quote" | null>(null); // 宛先選択ポップオーバー

  const selectedItem = items.find((i) => i.id === itemId) ?? null;
  const selectedIndustry = industries.find((i) => i.id === industryId) ?? null;
  const selectedShelf = getShelfForItem(selectedItem);
  const selectedTone = industryTone(selectedIndustry);
  const filteredItems = useMemo(() => getItemsForShelf(items, shelfId), [items, shelfId]);
  const featuredLibraryImage = selectedLibraryImage ?? libraryImages[0] ?? null;
  const featuredLibraryIsSelected =
    featuredLibraryImage != null && selectedLibraryImage?.id === featuredLibraryImage.id;
  const unit = selectedItem?.default_unit ?? "個";

  useEffect(() => {
    if (selectedItem && !filteredItems.some((item) => item.id === selectedItem.id)) {
      setShelfId(getShelfForItem(selectedItem).id);
    }
  }, [filteredItems, selectedItem]);

  useEffect(() => {
    setQuote(null);
    setRanges([]);
    setQuoteNote(null);
    setQuoteErr(null);
    setQuoteFallback(false);
    setFallbackNote(null);
    setPriceOverrides({});
  }, [industryId, itemId, qty]);

  useEffect(() => {
    setSelectedLibraryImage(null);
    setLibraryImages([]);
    if (!industryId && !itemId) return;
    const ac = new AbortController();
    const q = new URLSearchParams({ limit: "16" });
    if (industryId) q.set("industryId", industryId);
    if (itemId) q.set("itemId", itemId);
    setLibraryLoading(true);
    fetch(`/api/ai-images?${q.toString()}`, { signal: ac.signal })
      .then((res) => res.json())
      .then((data: { ok?: boolean; images?: ReferenceImageAsset[] }) => {
        if (data.ok) setLibraryImages(data.images ?? []);
      })
      .catch((e: unknown) => {
        if (!(e instanceof DOMException && e.name === "AbortError")) setLibraryImages([]);
      })
      .finally(() => {
        if (!ac.signal.aborted) setLibraryLoading(false);
      });
    return () => ac.abort();
  }, [industryId, itemId]);

  async function runQuote() {
    if (!industryId || !itemId) {
      setQuoteErr("市場（業界）とアイテムを選んでください");
      return;
    }
    setLoadingQuote(true);
    setQuoteErr(null);
    setQuote(null);
    setQuoteNote(null);
    setQuoteFallback(false);
    setFallbackNote(null);
    try {
      const res = await fetch(
        `/api/price-ranges?industryId=${industryId}&itemId=${itemId}&qty=${qty}`,
      );
      const data = await res.json();
      if (!data.ok) throw new Error(data.error ?? "取得に失敗しました");
      setRanges(data.ranges ?? []);
      setQuote(data.quote ?? null);
      setQuoteFallback(data.fallback === true);
      if (data.fallback === true) setFallbackNote(data.note ?? null);
      else if (data.note) setQuoteNote(data.note);
      if ((data.ranges ?? []).length === 0) {
        setQuoteNote(
          data.note ??
            "この市場×アイテムの上代レンジが未登録です。マスタ管理で登録すると見積もりできます。",
        );
      }
    } catch (e) {
      setQuoteErr(e instanceof Error ? e.message : String(e));
    } finally {
      setLoadingQuote(false);
    }
  }

  // 画像ファイル1枚をdataURLに読み込んで参考画像にセット（file入力・D&D・貼り付けで共用）
  function loadRefImageFile(file: File | null | undefined) {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setGenError("画像ファイルを指定してください（PNG / JPEG など）");
      setGenStatus("error");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setRefImage(typeof reader.result === "string" ? reader.result : null);
    reader.readAsDataURL(file);
  }

  function onPickRefImage(e: React.ChangeEvent<HTMLInputElement>) {
    loadRefImageFile(e.target.files?.[0]);
  }

  // ドロップゾーンへの画像ドラッグ&ドロップ
  function onDropRefImage(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setRefDragging(false);
    const file = e.dataTransfer.files?.[0];
    loadRefImageFile(file);
  }

  // ドロップゾーンで Cmd/Ctrl+V の画像貼り付けにも対応
  function onPasteRefImage(e: React.ClipboardEvent<HTMLDivElement>) {
    const item = Array.from(e.clipboardData.items).find((it) => it.type.startsWith("image/"));
    if (item) {
      e.preventDefault();
      loadRefImageFile(item.getAsFile());
    }
  }

  function pickShelf(nextShelfId: string) {
    setShelfId(nextShelfId);
    if (nextShelfId === "all") return;
    const nextItems = getItemsForShelf(items, nextShelfId);
    if (nextItems.length === 0) {
      setItemId("");
    } else if (!nextItems.some((item) => item.id === itemId)) {
      setItemId(nextItems[0].id);
    }
  }

  function applyIndustryTone() {
    if (!selectedTone) return;
    setRefNote((prev) => {
      const current = prev.trim();
      return current ? `${current}\n${selectedTone.prompt}` : selectedTone.prompt;
    });
  }

  function pickLibraryImage(img: ReferenceImageAsset) {
    setSelectedLibraryImage((prev) => (prev?.id === img.id ? null : img));
    if (!refNote.trim()) {
      const hints = [img.keywords, img.reference_note, selectedTone?.prompt].filter(Boolean);
      if (hints.length > 0) setRefNote(String(hints[0]));
    }
  }

  function adoptLibraryImage(img: ReferenceImageAsset) {
    setGenStatus("done");
    setGenImages((prev) => {
      if (prev.some((p) => p.url === img.url)) return prev;
      return [
        {
          id: img.id,
          url: img.url,
          provider: img.provider ?? "library",
          isMock: false,
        },
        ...prev,
      ];
    });
    setSelectedImages((prev) => (prev.includes(img.url) ? prev : [...prev, img.url]));
    setSelectedLibraryImage(img);
  }

  async function runGenerate() {
    if (!selectedItem) {
      setGenError("先にアイテムを選んでください");
      setGenStatus("error");
      return;
    }
    setGenStatus("loading");
    setGenImages([]);
    setGenNote(null);
    setGenError(null);
    setSelectedImages([]); // 新しい生成をしたら前回の採用はクリア
    try {
      const referenceImages = [
        ...(selectedLibraryImage
          ? [{ url: selectedLibraryImage.url, note: refNote || selectedLibraryImage.keywords || undefined }]
          : []),
        ...(refImage ? [{ dataUrl: refImage, note: refNote || undefined }] : []),
      ];
      const res = await fetch("/api/generate-image", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          itemName: selectedItem.name,
          marketName: selectedIndustry?.sub_name ?? undefined,
          keywords: refNote || undefined,
          count: genCount,
          referenceImages: referenceImages.length > 0 ? referenceImages : undefined,
          provider, // ②生成AI選択（env より優先）
          itemId: itemId || undefined, // ③履歴紐付け・⑧再利用キー
          industryId: industryId || undefined,
        }),
      });
      const data = await res.json();
      if (!data.ok) throw new Error(data.error ?? "画像生成に失敗しました");
      setGenImages(data.images ?? []);
      setGenNote(data.note ?? null);
      setGenStatus("done");
    } catch (e) {
      setGenError(e instanceof Error ? e.message : String(e));
      setGenStatus("error");
    }
  }

  // 画像の採用トグル（選択順を保つ）。既に採用済みなら外す。
  function toggleImage(url: string) {
    setSelectedImages((prev) =>
      prev.includes(url) ? prev.filter((u) => u !== url) : [...prev, url],
    );
  }

  function addToBasket() {
    if (!selectedItem || !quote) return;
    const item: ProposalItem = {
      item_id: itemId,
      industry_id: industryId,
      product_name: selectedItem.name,
      // 提案書に載せる商品情報（カゴ内で編集可能）。日数の初期値は受領サンプル準拠
      spec: { lines: [] },
      schedule: { sample_days: 15, production_days: 40 },
      selling_points: [],
      tooling_fee: ranges[0]?.default_tooling_fee ?? null,
      sample_fee: ranges[0]?.default_sample_fee ?? null,
      // ロット別単価: 推奨値に営業の確定単価(priceOverrides)をマージ。未入力の行は推奨を確定値に採用。
      tiers: buildTiers(ranges).map((t, i) => {
        const ov = priceOverrides[i];
        const finalPrice = ov === "" || ov == null ? t.unit_price : Number(ov);
        return {
          qty: t.qty,
          unit_price: finalPrice, // 確定＠単価
          recommended_price: t.recommended_price, // 推奨（区別用）
          is_overridden: finalPrice !== t.recommended_price,
          unit_cost: t.unit_cost,
          calc_meta: { recommended: t.recommended_price }, // DBにも推奨根拠を残す
        };
      }),
      // 複数採用画像（選択順=表示順、先頭をメインに）。ai_image_id は生成履歴からリンク
      images: selectedImages.map((url, idx) => ({
        url,
        is_primary: idx === 0,
        ai_image_id: genImages.find((g) => g.url === url)?.id ?? null,
      })),
      imageUrl: selectedImages[0] ?? null, // 後方互換（primaryショートカット）
    };
    setBasket((b) => [...b, item]);
    setSaveState(`「${selectedItem.name}」を提案に追加しました（${basket.length + 1}件）`);
  }

  async function saveProposal() {
    if (basket.length === 0) return;
    setSaveState("保存中…");
    try {
      const res = await fetch("/api/proposals", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title: `${selectedIndustry?.sub_name ?? "提案"} ご提案`,
          client_name: clientName || undefined,
          items: basket,
          status: "draft",
        }),
      });
      const data = await res.json();
      if (!data.ok) throw new Error(data.error ?? "保存に失敗しました");
      setSaveState(`✓ 提案を保存しました（ID: ${String(data.id).slice(0, 8)}…）`);
      setBasket([]);
    } catch (e) {
      setSaveState("⚠ 保存に失敗: " + (e instanceof Error ? e.message : String(e)));
    }
  }

  function updateBasketItem(index: number, patch: Partial<ProposalItem>) {
    setBasket((arr) => arr.map((it, i) => (i === index ? { ...it, ...patch } : it)));
  }

  async function exportPdf(kind: "proposal" | "quote", audience: "internal" | "client") {
    if (basket.length === 0) return;
    setPdfMenu(null);
    setPdfBusy(true);
    setSaveState(null);
    try {
      const pdf = await import("../lib/pdf");
      const header = {
        title: `${selectedIndustry?.sub_name ?? "提案"} ご提案`,
        clientName: clientName || null,
        salesRep: salesRep || null,
      };
      const bytes =
        kind === "proposal"
          ? await pdf.generateProposalPdf(header, basket, { audience })
          : await pdf.generateQuotePdf(header, basket, { audience });
      const d = new Date();
      const ymd = `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, "0")}${String(d.getDate()).padStart(2, "0")}`;
      const base = clientName || selectedIndustry?.sub_name || "提案";
      const docLabel = kind === "proposal" ? "提案書" : "見積書";
      const suffix = audience === "internal" ? "_社内" : "";
      pdf.downloadPdf(bytes, `${docLabel}_${base}_${ymd}${suffix}.pdf`);
      setSaveState(`✓ ${docLabel}PDF（${audience === "client" ? "客先提出用" : "社内確認用"}）を出力しました`);
    } catch (e) {
      setSaveState("⚠ PDF出力に失敗: " + (e instanceof Error ? e.message : String(e)));
    } finally {
      setPdfBusy(false);
    }
  }

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <Eyebrow>Sales Bench — 業界別グッズ提案</Eyebrow>
          <h2 className="text-2xl font-semibold tracking-tight mt-1">営業提案ワークベンチ</h2>
          <p className="text-sm text-ink-muted mt-1 max-w-[68ch]">
            業界・アイテム棚・参考画像をまとめて選び、見積と商品イメージを同じ流れで作ります。
          </p>
        </div>
        <div className="grid grid-cols-3 gap-2 text-xs md:min-w-[360px]">
          <div className="rounded border border-ink/10 bg-white px-3 py-2">
            <p className="text-ink-muted">業界</p>
            <p className="mt-1 font-medium truncate">{industryLabel(selectedIndustry)}</p>
          </div>
          <div className="rounded border border-ink/10 bg-white px-3 py-2">
            <p className="text-ink-muted">棚</p>
            <p className="mt-1 font-medium truncate">{selectedItem ? selectedShelf.shortLabel : "未選択"}</p>
          </div>
          <div className="rounded border border-ink/10 bg-white px-3 py-2">
            <p className="text-ink-muted">提案カゴ</p>
            <p className="mt-1 font-medium">{basket.length} 商品</p>
          </div>
        </div>
      </div>

      <HowTo title="使い方 — 上から順に選ぶだけです">
        <ol className="space-y-3">
          <HowToStep n={1}>
            <b>業界 → 棚 → アイテム</b>を選ぶ。棚はアイテムの置き場所（ライブ必需品・フォトカードなど）です。
          </HowToStep>
          <HowToStep n={2}>
            <b>Step 2 で相場と推奨単価が自動で出ます。</b>ロットを変えると単価も変わります。
            <span className="block text-xs mt-0.5">
              「未登録です」と出たら <b>マスタ管理</b> タブで登録してください（そちらにも使い方があります）。登録しなくても参考値で提案は作れます。
            </span>
          </HowToStep>
          <HowToStep n={3}>
            推奨の隣の<b>確定＠単価</b>に自分の値を入れると、提案書・見積書にはその値が載ります。空なら推奨のままです。
          </HowToStep>
          <HowToStep n={4}>
            <b>Step 3 で商品イメージを生成。</b>参考画像を選ぶ/貼ると、それに寄せた絵が出ます。枚数は 2〜6 枚から選べます。
            <span className="block text-xs mt-0.5">1枚ごとに費用がかかるので、まず少なく試して当たりを見てから増やすのがおすすめです。</span>
          </HowToStep>
          <HowToStep n={5}>
            採用する画像を選んで<b>提案カゴ</b>へ。複数商品をカゴに入れてから提案書にまとめられます。
          </HowToStep>
        </ol>
        <p className="mt-4 border-t border-ink/10 pt-3 text-xs">
          <b>お客様に画面を見せるとき：</b>右上の<b>商談モード</b>をオンにすると、原価・粗利・値引き下限・競合レンジが隠れます。
        </p>
      </HowTo>

      <Card className="p-6 space-y-6">
        <section>
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-sm font-medium">1. 業界を選ぶ</p>
              {selectedTone && (
                <p className="mt-1 text-xs text-ink-muted">{selectedTone.sellingAngle}</p>
              )}
            </div>
            {selectedTone && (
              <button
                type="button"
                onClick={applyIndustryTone}
                className="shrink-0 rounded-full bg-white px-3 py-1.5 text-xs font-medium ring-1 ring-ink/15 hover:ring-ink/40"
              >
                トーンを指示に入れる
              </button>
            )}
          </div>
          <div className="mt-3 grid grid-cols-2 gap-2 md:grid-cols-3">
            {industries.map((industry) => {
              const active = industry.id === industryId;
              const tone = industryTone(industry);
              return (
                <button
                  key={industry.id}
                  type="button"
                  onClick={() => setIndustryId(industry.id)}
                  className={`min-h-[74px] rounded border px-3 py-2 text-left transition ${
                    active
                      ? "border-ink bg-ink text-white"
                      : "border-ink/10 bg-white hover:border-ink/35"
                  }`}
                >
                  <span className="block text-sm font-medium">{industry.sub_name}</span>
                  {tone && (
                    <span className={`mt-1 block text-xs ${active ? "text-white/75" : "text-ink-muted"}`}>
                      {tone.title}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </section>

        <section>
          <p className="text-sm font-medium">2. アイテム棚を選ぶ</p>
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => pickShelf("all")}
              className={`rounded-full px-3 py-1.5 text-sm ring-1 transition ${
                shelfId === "all" ? "bg-ink text-white ring-ink" : "bg-white text-ink ring-ink/15 hover:ring-ink/40"
              }`}
            >
              すべて {items.length}
            </button>
            {ITEM_SHELVES.map((shelf) => {
              const shelfItems = getItemsForShelf(items, shelf.id);
              const active = shelf.id === shelfId;
              return (
                <button
                  key={shelf.id}
                  type="button"
                  onClick={() => pickShelf(shelf.id)}
                  className={`rounded-full px-3 py-1.5 text-sm ring-1 transition ${
                    active ? "bg-ink text-white ring-ink" : "bg-white text-ink ring-ink/15 hover:ring-ink/40"
                  }`}
                >
                  {shelf.shortLabel} {shelfItems.length}
                </button>
              );
            })}
          </div>
          {shelfId !== "all" && (
            <p className="mt-2 text-xs text-ink-muted">
              {ITEM_SHELVES.find((shelf) => shelf.id === shelfId)?.description}
            </p>
          )}
        </section>

        <section className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_280px]">
          <div>
            <p className="text-sm font-medium">3. アイテムを選ぶ</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {filteredItems.map((item) => {
                const active = item.id === itemId;
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => setItemId(item.id)}
                    className={`rounded-full px-3 py-1.5 text-sm ring-1 transition ${
                      active
                        ? "bg-ink text-white ring-ink"
                        : "bg-white text-ink ring-ink/15 hover:ring-ink/40"
                    }`}
                  >
                    {item.name}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <label className="block">
              <span className="text-sm font-medium">ロット数（{unit}）</span>
              <input
                type="number"
                min={1}
                value={qty}
                onChange={(e) => setQty(Number(e.target.value))}
                className="mt-2 w-full rounded px-3 py-2 text-sm ring-1 ring-ink/15"
                list="lot-presets"
              />
              <datalist id="lot-presets">
                {LOT_PRESETS.map((v) => (
                  <option key={v} value={v} />
                ))}
              </datalist>
            </label>
            <div className="mt-2 grid grid-cols-5 gap-1">
              {LOT_PRESETS.map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setQty(v)}
                  className={`rounded px-2 py-1 text-xs ring-1 transition ${
                    qty === v ? "bg-ink text-white ring-ink" : "bg-white text-ink-muted ring-ink/15 hover:ring-ink/35"
                  }`}
                >
                  {v}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={runQuote}
              disabled={loadingQuote || !industryId || !itemId}
              className="mt-3 w-full rounded-full bg-ink px-4 py-2 text-sm font-medium text-white hover:bg-ink/90 disabled:opacity-50"
            >
              {loadingQuote ? "計算中…" : "価格を確認"}
            </button>
          </div>
        </section>
        {quoteErr && (
          <p className="mt-3 text-sm text-red-700">⚠ {quoteErr}</p>
        )}
      </Card>

      {/* 見積もり結果 */}
      {quoteNote && !quote && (
        <Card className="p-6">
          <p className="text-sm text-ink-subtle">{quoteNote}</p>
        </Card>
      )}
      {quote && (
        <div className="space-y-4">
          <Eyebrow>Step 2 — 勝てる上代</Eyebrow>
          {quoteFallback && (
            <p className="text-xs text-amber-800 bg-amber-50 ring-1 ring-amber-200 rounded px-3 py-2">
              ⚠ {fallbackNote ??
                "この市場は相場未登録のため、他市場の同アイテム相場から算出した参考値です。マスタ管理で正式登録すると精度が上がります。"}
            </p>
          )}
          <Card className="p-6">
            <div className="flex flex-wrap items-baseline gap-x-8 gap-y-3">
              <div>
                <p className="text-xs text-ink-muted">推奨＠単価（{quote.qty.toLocaleString()}{unit}）</p>
                <p className="text-3xl font-semibold tracking-tight">
                  {formatYen(quote.recommendedUnitPrice)}
                </p>
              </div>
              {!hideCosts && (
                <>
                  <div>
                    <p className="text-xs text-ink-muted">競合レンジ</p>
                    <p className="text-lg">
                      {formatYen(quote.competitorLower)} 〜 {formatYen(quote.competitorUpper)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-ink-muted">粗利率（社内）</p>
                    <p className="text-lg">{Math.round(quote.grossMarginRate * 100)}%</p>
                  </div>
                  <div>
                    <p className="text-xs text-ink-muted">値引き下限（営業権限5%）</p>
                    <p className="text-lg">{formatYen(quote.minPriceSalesAuthority)}</p>
                  </div>
                </>
              )}
            </div>
            {!hideCosts && quote.warnings.length > 0 && (
              <div className="mt-4 space-y-1">
                {quote.warnings.map((w, i) => (
                  <p key={i} className="text-xs text-amber-700 bg-amber-50 ring-1 ring-amber-200 rounded px-3 py-2">
                    ⚠ {w}
                  </p>
                ))}
              </div>
            )}
          </Card>

          {/* ロット別＠単価表 */}
          {ranges.length > 0 && (
            <Card className="p-6">
              <p className="text-sm font-medium mb-1">ロット別＠単価（提案書に載る形）</p>
              <p className="text-xs text-ink-muted mb-3">
                推奨の隣に<b>確定＠単価</b>を入力すると、提案書・見積書にその値が載ります（未入力は推奨をそのまま採用）。
              </p>
              <MarketPriceLinks
                itemName={selectedItem?.name}
                marketName={selectedIndustry?.sub_name}
                className="mb-3"
              />
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-ink-muted border-b border-ink/10">
                    <th className="py-2 font-medium">ロット</th>
                    {!hideCosts && <th className="py-2 font-medium">競合レンジ</th>}
                    <th className="py-2 font-medium text-right">推奨＠単価</th>
                    <th className="py-2 font-medium text-right">確定＠単価</th>
                  </tr>
                </thead>
                <tbody>
                  {buildTiers(ranges).map((t, i) => {
                    const ov = priceOverrides[i];
                    const finalPrice = ov === "" || ov == null ? t.unit_price : Number(ov);
                    const changed = finalPrice !== t.recommended_price;
                    return (
                      <tr key={i} className="border-b border-ink/5">
                        <td className="py-2">{t.qty.toLocaleString()}{unit}〜</td>
                        {!hideCosts && (
                          <td className="py-2 text-ink-muted">
                            {formatYen(ranges[i]?.competitor_lower)} 〜 {formatYen(ranges[i]?.competitor_upper)}
                          </td>
                        )}
                        <td className="py-2 text-right text-ink-muted">{formatYen(t.recommended_price)}</td>
                        <td className="py-2 text-right">
                          <div className="flex items-center justify-end gap-2">
                            {changed && (
                              <span className="text-[10px] text-amber-700 bg-amber-50 ring-1 ring-amber-300 rounded px-1.5 py-0.5">
                                変更
                              </span>
                            )}
                            <input
                              type="number"
                              min={0}
                              value={priceOverrides[i] ?? ""}
                              placeholder={String(t.recommended_price ?? t.unit_price)}
                              onChange={(e) =>
                                setPriceOverrides((o) => ({
                                  ...o,
                                  [i]: e.target.value === "" ? "" : Number(e.target.value),
                                }))
                              }
                              className="w-24 ring-1 ring-ink/15 rounded px-2 py-1 text-sm text-right"
                            />
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <p className="text-xs text-ink-muted mt-2">
                ※参考価格です。仕様確定後に正式見積を提示します。
              </p>
            </Card>
          )}
        </div>
      )}

      {/* 画像生成（参考画像→デザイン） */}
      {itemId && (
        <div className="space-y-4">
          <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
            <div>
              <Eyebrow>Step 3 — 商品イメージを作る</Eyebrow>
              <h3 className="mt-1 text-xl font-semibold tracking-tight">ビジュアルワークベンチ</h3>
            </div>
            <div className="flex flex-wrap gap-2 text-xs">
              {selectedTone && (
                <span className="rounded-full bg-cyan-50 px-3 py-1.5 font-medium text-cyan-900 ring-1 ring-cyan-200">
                  {selectedTone.title}
                </span>
              )}
              {selectedLibraryImage && (
                <span className="rounded-full bg-rose-50 px-3 py-1.5 font-medium text-rose-900 ring-1 ring-rose-200">
                  参照中
                </span>
              )}
              {selectedImages.length > 0 && (
                <span className="rounded-full bg-emerald-50 px-3 py-1.5 font-medium text-emerald-900 ring-1 ring-emerald-200">
                  採用 {selectedImages.length}
                </span>
              )}
            </div>
          </div>

          <div className="grid items-start gap-5 lg:grid-cols-[minmax(0,1fr)_380px]">
            <section className="bg-white p-5 ring-1 ring-ink/10 shadow-[0_1px_0_rgba(10,10,10,0.04)]">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-medium">参考画像ライブラリ</p>
                  <p className="mt-1 text-xs text-ink-muted">
                    {`${industryLabel(selectedIndustry)} × ${selectedItem?.name ?? "アイテム"}`}
                  </p>
                </div>
                {selectedLibraryImage && (
                  <button
                    type="button"
                    onClick={() => setSelectedLibraryImage(null)}
                    className="shrink-0 rounded-full bg-white px-3 py-1.5 text-xs font-medium ring-1 ring-ink/15 hover:ring-ink/40"
                  >
                    参照を外す
                  </button>
                )}
              </div>

              {libraryLoading && (
                <div className="mt-4 grid gap-4 sm:grid-cols-2">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <div key={i} className="aspect-[4/3] bg-ink/[0.06] animate-pulse" />
                  ))}
                </div>
              )}

              {!libraryLoading && libraryImages.length > 0 && (
                <div className={`mt-4 grid gap-4 ${libraryImages.length === 1 ? "grid-cols-1" : "sm:grid-cols-2"}`}>
                  {libraryImages.map((img) => {
                    const active = selectedLibraryImage?.id === img.id;
                    return (
                      <article
                        key={img.id}
                        className={`group overflow-hidden bg-white ring-1 transition ${
                          active ? "ring-2 ring-ink" : "ring-ink/10 hover:ring-ink/35"
                        }`}
                      >
                        <button
                          type="button"
                          onClick={() => pickLibraryImage(img)}
                          className="relative block w-full bg-[radial-gradient(circle_at_25%_20%,rgba(34,211,238,0.18),transparent_32%),radial-gradient(circle_at_80%_20%,rgba(244,114,182,0.15),transparent_30%),linear-gradient(135deg,#ffffff,#f8fafc)]"
                        >
                          <img
                            src={img.url}
                            alt={img.item_name ?? "参考画像"}
                            className={`w-full object-contain p-4 transition duration-300 group-hover:scale-[1.03] ${
                              libraryImages.length === 1 ? "aspect-[16/10]" : "aspect-[4/3]"
                            }`}
                          />
                          {active && (
                            <span className="absolute left-3 top-3 rounded-full bg-ink px-2.5 py-1 text-xs font-medium text-white">
                              参照中
                            </span>
                          )}
                        </button>
                        <div className="flex items-center justify-between gap-3 border-t border-ink/10 p-3">
                          <div className="min-w-0">
                            <p className="truncate text-sm font-medium">
                              {img.item_name ?? selectedItem?.name ?? "参考画像"}
                            </p>
                            <p className="mt-0.5 truncate text-[11px] text-ink-muted">
                              {img.industry_label ?? "サンプル"}
                            </p>
                          </div>
                          <div className="flex shrink-0 gap-2">
                            <button
                              type="button"
                              onClick={() => pickLibraryImage(img)}
                              className="rounded-full bg-white px-3 py-1.5 text-xs font-medium ring-1 ring-ink/15 hover:ring-ink/40"
                            >
                              参照
                            </button>
                            <button
                              type="button"
                              onClick={() => adoptLibraryImage(img)}
                              className="rounded-full bg-ink px-3 py-1.5 text-xs font-medium text-white hover:bg-ink/90"
                            >
                              採用
                            </button>
                          </div>
                        </div>
                      </article>
                    );
                  })}
                </div>
              )}

              {!libraryLoading && libraryImages.length === 0 && (
                <div className="mt-4 border border-dashed border-ink/20 bg-white px-4 py-8 text-sm text-ink-muted">
                  近い参考画像はまだありません。アップロード画像または生成後の採用画像が次回以降の候補になります。
                </div>
              )}
            </section>

            <aside className="bg-white p-5 ring-1 ring-ink/10 shadow-[0_1px_0_rgba(10,10,10,0.04)] lg:sticky lg:top-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-medium">生成パネル</p>
                  <p className="mt-1 text-xs text-ink-muted">
                    {selectedLibraryImage
                      ? `${selectedLibraryImage.item_name ?? selectedItem?.name ?? "選択画像"}を参照中`
                      : "参考画像を選んで調整"}
                  </p>
                </div>
                {featuredLibraryImage && !featuredLibraryIsSelected && (
                  <button
                    type="button"
                    onClick={() => pickLibraryImage(featuredLibraryImage)}
                    className="shrink-0 rounded-full bg-cyan-50 px-3 py-1.5 text-xs font-medium text-cyan-900 ring-1 ring-cyan-200 hover:bg-cyan-100"
                  >
                    参照にする
                  </button>
                )}
              </div>

              <div className="mt-4 overflow-hidden bg-[radial-gradient(circle_at_20%_12%,rgba(34,211,238,0.20),transparent_30%),radial-gradient(circle_at_82%_18%,rgba(251,113,133,0.16),transparent_32%),linear-gradient(135deg,#ffffff,#f7f7f7)] ring-1 ring-ink/10">
                {featuredLibraryImage ? (
                  <img
                    src={featuredLibraryImage.url}
                    alt={featuredLibraryImage.item_name ?? "選択中の参考画像"}
                    className="aspect-square w-full object-contain p-5"
                  />
                ) : refImage ? (
                  <img src={refImage} alt="手元の参考画像" className="aspect-square w-full object-contain p-5" />
                ) : (
                  <div className="flex aspect-square items-center justify-center px-8 text-center text-sm text-ink-muted">
                    参考画像を選択
                  </div>
                )}
              </div>

              {featuredLibraryImage && (
                <div className="mt-3 space-y-1.5">
                  <p className="truncate text-sm font-medium">
                    {featuredLibraryImage.item_name ?? selectedItem?.name ?? "参考画像"}
                  </p>
                  <p className="line-clamp-2 text-xs text-ink-muted">
                    {featuredLibraryImage.reference_note ??
                      featuredLibraryImage.keywords ??
                      `${industryLabel(selectedIndustry)}向け`}
                  </p>
                  <div className="flex flex-wrap gap-2 pt-1">
                    {featuredLibraryImage.provider && (
                      <span className="rounded-full bg-ink/[0.04] px-2.5 py-1 text-[11px] text-ink-muted">
                        {featuredLibraryImage.provider}
                      </span>
                    )}
                    {featuredLibraryImage.keywords && (
                      <span className="rounded-full bg-ink/[0.04] px-2.5 py-1 text-[11px] text-ink-muted">
                        {featuredLibraryImage.keywords.split(",").slice(0, 2).join(" / ")}
                      </span>
                    )}
                  </div>
                </div>
              )}

              {selectedLibraryImage && (
                <button
                  type="button"
                  onClick={() => adoptLibraryImage(selectedLibraryImage)}
                  className="mt-4 w-full rounded-full bg-ink px-4 py-2.5 text-sm font-medium text-white hover:bg-ink/90"
                >
                  この画像を提案に採用
                </button>
              )}

              <div className="mt-5">
                <span className="text-sm font-medium">手元の参考画像</span>
                <div
                  onDragOver={(e) => {
                    e.preventDefault();
                    setRefDragging(true);
                  }}
                  onDragLeave={() => setRefDragging(false)}
                  onDrop={onDropRefImage}
                  onPaste={onPasteRefImage}
                  className={`mt-2 p-3 transition ring-1 ${
                    refDragging ? "ring-2 ring-ink/60 bg-ink/[0.03]" : "ring-ink/15"
                  }`}
                >
                  {refImage ? (
                    <div className="relative">
                      <img src={refImage} alt="参考画像" className="h-32 w-full object-contain" />
                      <button
                        type="button"
                        onClick={() => setRefImage(null)}
                        className="absolute right-0 top-0 rounded-full bg-white px-2 py-0.5 text-[10px] text-ink-muted ring-1 ring-ink/15 hover:ring-ink/40"
                      >
                        削除
                      </button>
                    </div>
                  ) : (
                    <p className="flex h-24 items-center justify-center px-3 text-center text-xs text-ink-muted">
                      画像アップロード
                    </p>
                  )}
                  <input type="file" accept="image/*" onChange={onPickRefImage} className="mt-2 w-full text-xs" />
                </div>
              </div>

              <label className="mt-5 block">
                <span className="text-sm font-medium">イメージ指示</span>
                <textarea
                  value={refNote}
                  onChange={(e) => setRefNote(e.target.value)}
                  placeholder={selectedTone?.prompt ?? "例: パステルカラーで可愛い系。桜モチーフ。ゆるキャラ風"}
                  className="mt-2 min-h-[140px] w-full resize-none px-3 py-2 text-sm ring-1 ring-ink/15"
                />
              </label>

              <div className="mt-4 flex flex-wrap items-center gap-3">
                <div className="inline-flex bg-white p-0.5 text-xs ring-1 ring-ink/15">
                  {(["openai", "gemini"] as const).map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setProvider(p)}
                      className={`px-3 py-1.5 font-medium transition ${
                        provider === p ? "bg-ink text-white" : "text-ink-muted hover:text-ink"
                      }`}
                    >
                      {p === "openai" ? "ChatGPT" : "Gemini"}
                    </button>
                  ))}
                </div>
                <label className="inline-flex items-center gap-2 text-xs text-ink-muted">
                  枚数
                  <div className="inline-flex bg-white p-0.5 ring-1 ring-ink/15">
                    {GEN_COUNTS.map((n) => (
                      <button
                        key={n}
                        type="button"
                        onClick={() => setGenCount(n)}
                        aria-pressed={genCount === n}
                        className={`min-w-8 px-2 py-1.5 font-medium transition ${
                          genCount === n ? "bg-ink text-white" : "text-ink-muted hover:text-ink"
                        }`}
                      >
                        {n}
                      </button>
                    ))}
                  </div>
                </label>
                <button
                  type="button"
                  onClick={runGenerate}
                  disabled={genStatus === "loading"}
                  className="min-h-10 flex-1 rounded-full bg-ink px-4 py-2.5 text-sm font-medium text-white hover:bg-ink/90 disabled:opacity-50"
                >
                  {genStatus === "loading" ? "生成中…" : `生成する（${genCount}枚）`}
                </button>
              </div>
            </aside>
          </div>

          {genStatus === "loading" && (
            <div className="grid gap-4 bg-white p-5 ring-1 ring-ink/10 md:grid-cols-2">
              {Array.from({ length: genCount }).map((_, i) => (
                <div key={i} className="aspect-square bg-ink/[0.06] animate-pulse" />
              ))}
            </div>
          )}

          {genStatus === "error" && (
            <div className="bg-red-500/[0.04] px-4 py-4 ring-1 ring-red-500/30">
              <p className="text-sm text-red-600">画像生成に失敗しました: {genError}</p>
              <button
                type="button"
                onClick={runGenerate}
                className="mt-3 rounded-full bg-white px-3 py-1.5 text-xs font-medium ring-1 ring-ink/15 hover:ring-ink/40"
              >
                再試行
              </button>
            </div>
          )}

          {genStatus === "done" && genNote && (
            <p className="bg-ink/[0.03] px-3 py-2 text-xs text-ink-muted">{genNote}</p>
          )}

          {genStatus === "done" && genImages.length > 0 && (
            <section className="bg-white p-5 ring-1 ring-ink/10 shadow-[0_1px_0_rgba(10,10,10,0.04)]">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-medium">生成結果</p>
                  <p className="mt-1 text-xs text-ink-muted">
                    採用した順に提案書へ並びます。
                  </p>
                </div>
                {selectedImages.length > 0 && (
                  <span className="rounded-full bg-emerald-50 px-3 py-1.5 text-xs font-medium text-emerald-900 ring-1 ring-emerald-200">
                    採用 {selectedImages.length}
                  </span>
                )}
              </div>
              <div className="mt-4 grid gap-4 md:grid-cols-2">
                {genImages.map((img, i) => {
                  const order = selectedImages.indexOf(img.url);
                  const active = order >= 0;
                  return (
                    <button
                      type="button"
                      key={i}
                      onClick={() => toggleImage(img.url)}
                      className={`relative overflow-hidden bg-white ring-2 transition ${
                        active ? "ring-ink" : "ring-ink/10 hover:ring-ink/30"
                      }`}
                    >
                      <img src={img.url} alt={`案${i + 1}`} className="aspect-square w-full object-cover" />
                      {active && (
                        <span className="absolute right-3 top-3 rounded-full bg-ink px-2.5 py-1 text-xs font-medium text-white">
                          採用{order + 1}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </section>
          )}
        </div>
      )}

      {/* 提案に追加 / 保存 / PDF出力 */}
      {quote && (
        <div className="space-y-3">
          {basket.length > 0 && (
            <div className="flex flex-wrap items-center gap-3">
              <label className="block">
                <span className="text-xs text-ink-muted">提案先（クライアント名）</span>
                <input
                  type="text"
                  value={clientName}
                  onChange={(e) => setClientName(e.target.value)}
                  placeholder="例: ◯◯株式会社"
                  className="mt-0.5 block w-56 ring-1 ring-ink/15 rounded px-3 py-1.5 text-sm"
                />
              </label>
              <label className="block">
                <span className="text-xs text-ink-muted">担当者名（PDF記載用）</span>
                <input
                  type="text"
                  value={salesRep}
                  onChange={(e) => setSalesRep(e.target.value)}
                  placeholder="例: 山田"
                  className="mt-0.5 block w-40 ring-1 ring-ink/15 rounded px-3 py-1.5 text-sm"
                />
              </label>
            </div>
          )}
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={addToBasket}
              className="px-4 py-2 rounded-full ring-1 ring-ink/15 bg-white text-sm font-medium hover:ring-ink/40 shadow-[0_1px_0_rgba(10,10,10,0.04)]"
            >
              ＋ この商品を提案に追加
            </button>
            {basket.length > 0 && (
              <>
                <button
                  type="button"
                  onClick={saveProposal}
                  className="px-4 py-2 rounded-full bg-ink text-white text-sm font-medium hover:bg-ink/90"
                >
                  提案を保存（{basket.length}商品）
                </button>
                <PdfButton
                  label="提案書PDF"
                  busy={pdfBusy}
                  open={pdfMenu === "proposal"}
                  onToggle={() => setPdfMenu((m) => (m === "proposal" ? null : "proposal"))}
                  onPick={(a) => exportPdf("proposal", a)}
                />
                <PdfButton
                  label="見積書PDF"
                  busy={pdfBusy}
                  open={pdfMenu === "quote"}
                  onToggle={() => setPdfMenu((m) => (m === "quote" ? null : "quote"))}
                  onPick={(a) => exportPdf("quote", a)}
                />
              </>
            )}
            {saveState && <span className="text-sm text-ink-subtle">{saveState}</span>}
          </div>
        </div>
      )}

      {/* 提案カゴの中身 */}
      {basket.length > 0 && (
        <Card className="p-6">
          <p className="text-sm font-medium mb-3">この提案に入っている商品（{basket.length}件）</p>
          <ul className="space-y-2">
            {basket.map((b, i) => {
              const schedule = (b.schedule ?? {}) as { sample_days?: number; production_days?: number };
              const specText = Array.isArray((b.spec as { lines?: string[] })?.lines)
                ? ((b.spec as { lines: string[] }).lines ?? []).join("\n")
                : "";
              return (
                <li key={i} className="text-sm border-b border-ink/5 pb-2">
                  <div className="flex items-center gap-3">
                    {(b.images?.length ? b.images.map((im) => im.url) : b.imageUrl ? [b.imageUrl] : []).map(
                      (url, k) => (
                        <img
                          key={k}
                          src={url}
                          alt=""
                          className="w-10 h-10 object-cover rounded ring-1 ring-ink/10"
                        />
                      ),
                    )}
                    <span className="font-medium">{b.product_name}</span>
                    <span className="text-ink-muted">
                      {b.tiers?.length ?? 0}段のロット別単価
                    </span>
                    {(b.tiers?.length ?? 0) > 0 && (
                      <label className="flex items-center gap-1 text-xs text-ink-muted">
                        見積数量
                        <select
                          value={b.quote_qty ?? b.tiers?.[0]?.qty ?? ""}
                          onChange={(e) => updateBasketItem(i, { quote_qty: Number(e.target.value) })}
                          className="ring-1 ring-ink/15 rounded px-2 py-0.5 text-xs bg-white"
                        >
                          {(b.tiers ?? []).map((t, ti) => (
                            <option key={ti} value={t.qty}>
                              {Number(t.qty).toLocaleString()}個 / ＠{Number(t.unit_price).toLocaleString()}円
                            </option>
                          ))}
                        </select>
                      </label>
                    )}
                    <button
                      type="button"
                      onClick={() => setBasket((arr) => arr.filter((_, idx) => idx !== i))}
                      className="ml-auto text-xs text-ink-muted hover:text-red-600"
                    >
                      削除
                    </button>
                  </div>
                  <details className="mt-2">
                    <summary className="text-xs text-ink-muted cursor-pointer select-none">
                      提案書の記載内容を編集（仕様・スケジュール・商品特徴）
                    </summary>
                    <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-3 pl-2">
                      <label className="block">
                        <span className="text-xs text-ink-muted">仕様（1行1項目）</span>
                        <textarea
                          value={specText}
                          onChange={(e) =>
                            updateBasketItem(i, { spec: { lines: e.target.value.split("\n") } })
                          }
                          placeholder={"素材：透明アクリル板 2mm\nサイズ：W60mm×H52.4mm\n加工：フルカラー印刷＋白\n包装：台紙＋OPP"}
                          className="mt-0.5 w-full ring-1 ring-ink/15 rounded px-3 py-2 text-xs min-h-[90px]"
                        />
                      </label>
                      <div className="space-y-3">
                        <div className="flex gap-3">
                          <label className="block">
                            <span className="text-xs text-ink-muted">サンプル（約◯日）</span>
                            <input
                              type="number"
                              min={0}
                              value={schedule.sample_days ?? ""}
                              onChange={(e) =>
                                updateBasketItem(i, {
                                  schedule: { ...schedule, sample_days: Number(e.target.value) },
                                })
                              }
                              className="mt-0.5 w-24 ring-1 ring-ink/15 rounded px-3 py-1.5 text-xs"
                            />
                          </label>
                          <label className="block">
                            <span className="text-xs text-ink-muted">校了後（約◯日）</span>
                            <input
                              type="number"
                              min={0}
                              value={schedule.production_days ?? ""}
                              onChange={(e) =>
                                updateBasketItem(i, {
                                  schedule: { ...schedule, production_days: Number(e.target.value) },
                                })
                              }
                              className="mt-0.5 w-24 ring-1 ring-ink/15 rounded px-3 py-1.5 text-xs"
                            />
                          </label>
                        </div>
                        <label className="block">
                          <span className="text-xs text-ink-muted">商品特徴（1行1点・提案書の《商品特徴》欄）</span>
                          <textarea
                            value={(b.selling_points ?? []).join("\n")}
                            onChange={(e) =>
                              updateBasketItem(i, { selling_points: e.target.value.split("\n") })
                            }
                            placeholder={"層を分けた印刷表現で奥行きのある仕上がり\n種類アソートで生産可能（3種×各100個など）"}
                            className="mt-0.5 w-full ring-1 ring-ink/15 rounded px-3 py-2 text-xs min-h-[60px]"
                          />
                        </label>
                      </div>
                    </div>
                  </details>
                </li>
              );
            })}
          </ul>
        </Card>
      )}
    </div>
  );
}

// PDF出力ボタン。押すと「社内確認用 / 客先提出用」の2択ポップオーバーを出す
function PdfButton({
  label,
  busy,
  open,
  onToggle,
  onPick,
}: {
  label: string;
  busy: boolean;
  open: boolean;
  onToggle: () => void;
  onPick: (audience: "internal" | "client") => void;
}) {
  return (
    <span className="relative inline-block">
      <button
        type="button"
        onClick={onToggle}
        disabled={busy}
        className="px-4 py-2 rounded-full ring-1 ring-ink/15 bg-white text-sm font-medium hover:ring-ink/40 disabled:opacity-50"
      >
        {busy ? "PDF生成中…" : label}
      </button>
      {open && !busy && (
        <div className="absolute z-10 mt-1 left-0 w-48 rounded-lg bg-white ring-1 ring-ink/15 shadow-lg p-1">
          <button
            type="button"
            onClick={() => onPick("client")}
            className="block w-full text-left px-3 py-2 rounded text-sm hover:bg-ink/[0.04]"
          >
            客先提出用
          </button>
          <button
            type="button"
            onClick={() => onPick("internal")}
            className="block w-full text-left px-3 py-2 rounded text-sm hover:bg-ink/[0.04]"
          >
            社内確認用<span className="text-xs text-ink-muted">（社外秘フッタ）</span>
          </button>
        </div>
      )}
    </span>
  );
}

// レンジ配列から「ロット別＠単価」行を作る。各帯の推奨単価は競合上限クランプ済みの近似
function buildTiers(ranges: PriceRange[]) {
  return ranges.map((r) => {
    // フロント側は簡易に「原価上限から目標粗利35%→競合上限クランプ→競合下限クランプ」
    const cost = r.cost_upper ?? r.cost_lower ?? r.competitor_lower;
    let p = Math.min(cost / (1 - 0.35), r.competitor_upper);
    p = Math.max(p, r.competitor_lower);
    const price = r.recommended_override ?? (p < 10 ? Math.round(p) : Math.round(p / 10) * 10);
    return {
      qty: r.lot_min,
      unit_price: price, // 初期値=推奨。確定単価は addToBasket で overrides によって上書きされる
      recommended_price: price, // 推奨（区別用・不変）
      unit_cost: r.cost_upper ?? r.cost_lower ?? null,
    };
  });
}
